// src/services/api/volunteerService.ts
import type { Volunteer } from "../../types/volunteer";
import type { VolunteerCountResponse } from "../../types/volunteerCount";
import { awsConfig } from "../config/aws-config";
import { toast } from "react-toastify";

/** Payload for creating a volunteer (no auth needed). */
interface CreateVolunteerPayload {
  name: string;
  email: string;
  phone?: string;
  education?: string;
  interests?: string[];
  otherInterest?: string;
  hoursAvailable?: string;
  language?: string;
  proficiency?: string;
  referralSource?: string;
  comments?: string;
}

/** Generic error parser. */
const parseErrorResponse = async (
  response: Response
): Promise<{ message: string }> => {
  try {
    const data = await response.json();
    return { message: data.message || "An error occurred" };
  } catch {
    return { message: response.statusText || "An error occurred" };
  }
};

export const volunteerService = {
  /**
   * Creates a volunteer record (no token needed).
   * Endpoint: POST /volunteers
   */
  createVolunteer: async (
    data: CreateVolunteerPayload
  ): Promise<Volunteer> => {
    const url = `${awsConfig.apiEndpoint}/volunteers`;
    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to create volunteer");
      }
      const volunteer = await response.json();
      return volunteer;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves volunteers (requires token).
   * Endpoint: GET /admin/volunteers
   *
   * - If token is "sample-token", the response is expected to be of type VolunteerCountResponse.
   * - Otherwise, the response is expected to be: { success: boolean, volunteers: Volunteer[] }
   *   or directly an array of volunteers.
   */
  getVolunteers: async (
    token: string
  ): Promise<VolunteerCountResponse | Volunteer[] | { success: boolean, volunteers: Volunteer[] }> => {

    const url = `${awsConfig.apiEndpoint}/admin/volunteers`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch volunteers");
      }

      const data = await response.json();

      // When token is "sample-token", we expect a VolunteerCountResponse.
      if (token === "sample-token") {
        if (!data.success || data.totalVolunteers === undefined) {
          throw new Error("Invalid response format from server");
        }
        return data as VolunteerCountResponse;
      } else {
        // Handle both response formats:
        // 1. { success: boolean, volunteers: Volunteer[] }
        // 2. Volunteer[] directly
        if (Array.isArray(data)) {
          return data as Volunteer[];
        } else if (data.success && Array.isArray(data.volunteers)) {
          return data as { success: boolean, volunteers: Volunteer[] };
        } else {
          throw new Error("Invalid response format from server");
        }
      }
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves a single volunteer by ID (requires token).
   * Endpoint: GET /admin/volunteers/{id}
   */
  getVolunteerById: async (
    token: string,
    volunteerId: string
  ): Promise<Volunteer> => {
    const url = `${awsConfig.apiEndpoint}/admin/volunteers/${volunteerId}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch volunteer");
      }

      // The server returns { success: boolean, volunteer: {...} }
      const data = await response.json();
      if (!data.success || !data.volunteer) {
        throw new Error("Invalid response format from server");
      }
      
      return data.volunteer;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Updates a volunteer (requires token).
   * Endpoint: PUT /admin/volunteers/{id}
   */
  updateVolunteer: async (
    token: string,
    volunteerId: string,
    data: Partial<Volunteer>
  ): Promise<Volunteer> => {
    const url = `${awsConfig.apiEndpoint}/admin/volunteers/${volunteerId}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to update volunteer");
      }
      return response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Deletes a volunteer (requires token).
   * Endpoint: DELETE /admin/volunteers/{id}
   */
  deleteVolunteer: async (
    token: string,
    volunteerId: string
  ): Promise<{ success: boolean }> => {
    const url = `${awsConfig.apiEndpoint}/admin/volunteers/${volunteerId}`;
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to delete volunteer");
      }
      return response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },
};
